import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import {
  ModalShellComponent,
  type ModalShellSize,
} from '@automatick/shared-presentation-ng';
import {
  GlobalClientService,
  OnboardingClientService,
  type SaveUserOnboardingStepRequest,
  type SelectOption,
  type TimeZone,
  type UserOnboardingCommunicationChannelResponse,
  type UserOnboardingDeviceConnectorRecommendationResponse,
  type UserOnboardingSetupSnapshot,
  type UserOnboardingStatusResponse,
} from '@automatick/shared-ng';
import { ExternalNavigationPolicyKind } from '@automatick/shared-ts';
import { ButtonDirective, SelectComponent } from '@seech/controls-ng';
import { IconDirective, type Icon } from '@seech/icons-ng';
import { ModalService, StepComponent, StepperComponent } from '@seech/ux-ng';
import {
  catchError,
  defer,
  EMPTY,
  finalize,
  forkJoin,
  from,
  map,
  Observable,
  of,
  switchMap,
} from 'rxjs';
import { ExternalNavigationService } from '../../../services/external-navigation.service';

type OnboardingStepKey =
  | 'proactive-assistant'
  | 'time-zone'
  | 'communication-channels'
  | 'devices-connectors'
  | 'emergency';

interface OnboardingStepView {
  readonly key: OnboardingStepKey;
  readonly label: string;
  readonly icon: Icon;
}

interface OnboardingOperationSnapshotBase {
  readonly generation: number;
  readonly kind: 'load' | 'step' | 'dismiss' | 'navigation';
}

interface OnboardingLoadSnapshot extends OnboardingOperationSnapshotBase {
  readonly kind: 'load';
}

interface OnboardingStepBodySnapshot {
  readonly stepKey: OnboardingStepKey;
  readonly skip: boolean;
  readonly proactiveAssistantEnabled?: boolean;
  readonly timeZoneId?: string;
  readonly preferredCommunicationChannels?: readonly string[];
}

interface OnboardingStepOperationSnapshot extends OnboardingOperationSnapshotBase {
  readonly kind: 'step';
  readonly stepIndex: number;
  readonly isFinalStep: boolean;
  readonly body: OnboardingStepBodySnapshot;
  readonly completionBody: Readonly<{ completedOn: string }> | null;
}

interface OnboardingDismissSnapshot extends OnboardingOperationSnapshotBase {
  readonly kind: 'dismiss';
  readonly body: Readonly<{ dismissedOn: string }>;
}

interface OnboardingNavigationSnapshot extends OnboardingOperationSnapshotBase {
  readonly kind: 'navigation';
  readonly route: string;
}

type OnboardingOperationSnapshot =
  | OnboardingLoadSnapshot
  | OnboardingStepOperationSnapshot
  | OnboardingDismissSnapshot
  | OnboardingNavigationSnapshot;

type StepMutationOutcome =
  | {
      readonly kind: 'saved';
      readonly status: UserOnboardingStatusResponse;
    }
  | {
      readonly kind: 'completed';
      readonly savedStatus: UserOnboardingStatusResponse;
      readonly status: UserOnboardingStatusResponse;
    }
  | {
      readonly kind: 'failed';
      readonly phase: 'save' | 'complete';
      readonly savedStatus?: UserOnboardingStatusResponse;
    };

@Component({
  selector: 'app-onboarding-stepper-modal',
  standalone: true,
  imports: [
    ButtonDirective,
    FormsModule,
    IconDirective,
    ModalShellComponent,
    SelectComponent,
    StepComponent,
    StepperComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './onboarding-stepper-modal.component.html',
  styleUrl: './onboarding-stepper-modal.component.scss',
})
export class OnboardingStepperModalComponent implements OnInit {
  readonly modalSize: ModalShellSize = 'wide';
  readonly steps: readonly OnboardingStepView[] = Object.freeze([
    Object.freeze({ key: 'time-zone', label: 'Time zone', icon: 'globe' }),
    Object.freeze({
      key: 'proactive-assistant',
      label: 'Assistant',
      icon: 'dashboard-circle',
    }),
    Object.freeze({
      key: 'communication-channels',
      label: 'Channels',
      icon: 'messaging',
    }),
    Object.freeze({
      key: 'devices-connectors',
      label: 'Connectors',
      icon: 'hub',
    }),
    Object.freeze({ key: 'emergency', label: 'Emergency', icon: 'warning' }),
  ] satisfies readonly OnboardingStepView[]);

  status: UserOnboardingStatusResponse | null = null;
  activeStepIndex = 0;
  proactiveAssistantEnabled = true;
  timeZoneId = '';
  timeZoneOptions: SelectOption[] = [];
  selectedChannels = new Set<string>(['InApp']);
  isLoading = true;
  isSubmitting = false;
  isNavigating = false;
  isDismissing = false;
  errorMessage = '';

  private readonly onboardingClient = inject(OnboardingClientService);
  private readonly globalClient = inject(GlobalClientService);
  private readonly modalService = inject(ModalService);
  private readonly externalNavigation = inject(ExternalNavigationService);
  private readonly router = inject(Router);
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private operationGeneration = 0;
  private activeOperation: OnboardingOperationSnapshot | null = null;
  private destroyed = false;
  private dismissalStarted = false;
  private resultClosed = false;

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.destroyed = true;
      this.operationGeneration += 1;
      this.activeOperation = null;
    });
  }

  ngOnInit(): void {
    const operation: OnboardingLoadSnapshot = Object.freeze({
      generation: this.nextOperationGeneration(),
      kind: 'load',
    });
    this.ownOperation(operation);

    forkJoin({
      status: this.onboardingClient.get(),
      timeZones: this.globalClient
        .getTimeZones({ size: 1000 })
        .pipe(catchError(() => of([] as TimeZone[]))),
    })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.finalizeOperation(operation)),
      )
      .subscribe({
        next: ({ status, timeZones }) => {
          if (!this.isCurrentOperation(operation)) {
            return;
          }

          this.timeZoneOptions = this.toTimeZoneOptions(timeZones);
          this.applyStatus(status);
          this.activeStepIndex = this.resolveFirstIncompleteStepIndex(status);
          this.requestRender();
        },
        error: () => {
          if (!this.isCurrentOperation(operation)) {
            return;
          }

          this.errorMessage = 'Unable to load setup details.';
          this.requestRender();
        },
      });
  }

  get activeStep(): OnboardingStepView {
    return this.steps[this.activeStepIndex] ?? this.steps[0];
  }

  get setup(): UserOnboardingSetupSnapshot | undefined {
    return this.status?.setup;
  }

  get communicationChannels(): readonly UserOnboardingCommunicationChannelResponse[] {
    return this.setup?.communicationChannels ?? [];
  }

  get deviceRecommendations(): readonly UserOnboardingDeviceConnectorRecommendationResponse[] {
    return this.setup?.deviceConnectorRecommendations ?? [];
  }

  get emergencyContactCount(): number {
    return this.setup?.emergency?.emergencyContactCount ?? 0;
  }

  get emergencyContactMethodCount(): number {
    return this.setup?.emergency?.emergencyContactMethodCount ?? 0;
  }

  get isFirstStep(): boolean {
    return this.activeStepIndex === 0;
  }

  get isLastStep(): boolean {
    return this.activeStepIndex === this.steps.length - 1;
  }

  get progressPercent(): number {
    const finalStepIndex = this.steps.length - 1;
    if (finalStepIndex <= 0) {
      return 0;
    }

    const boundedIndex = Math.min(
      Math.max(this.activeStepIndex, 0),
      finalStepIndex,
    );
    return (boundedIndex / finalStepIndex) * 100;
  }

  get isBusy(): boolean {
    return this.isSubmitting || this.isNavigating || this.isDismissing;
  }

  isStepComplete(stepKey: OnboardingStepKey): boolean {
    return this.status?.completedStepKeys?.includes(stepKey) === true;
  }

  isStepSkipped(stepKey: OnboardingStepKey): boolean {
    return this.status?.skippedStepKeys?.includes(stepKey) === true;
  }

  isChannelSelected(channelKind: string | null | undefined): boolean {
    return !!channelKind && this.selectedChannels.has(channelKind);
  }

  formatChannelKind(channelKind: string | null | undefined): string {
    switch ((channelKind ?? '').toLowerCase()) {
      case 'inapp':
        return 'In App';
      case 'sms':
        return 'SMS';
      case 'whatsapp':
        return 'WhatsApp';
      default:
        return channelKind ?? '';
    }
  }

  channelIcon(channelKind: string | null | undefined): Icon {
    switch ((channelKind ?? '').toLowerCase()) {
      case 'email':
        return 'alt-email';
      case 'sms':
      case 'phone':
        return 'phone';
      case 'whatsapp':
        return 'chat';
      default:
        return 'messaging';
    }
  }

  toggleChannel(channel: UserOnboardingCommunicationChannelResponse): void {
    const channelKind = channel.channelKind;
    if (
      this.resultClosed ||
      this.isBusy ||
      !channelKind ||
      !channel.isAvailable ||
      channelKind === 'InApp'
    ) {
      return;
    }

    if (this.selectedChannels.has(channelKind)) {
      this.selectedChannels.delete(channelKind);
    } else {
      this.selectedChannels.add(channelKind);
    }
  }

  selectStep(index: number): void {
    if (
      this.resultClosed ||
      this.isBusy ||
      index < 0 ||
      index >= this.steps.length
    ) {
      return;
    }

    this.activeStepIndex = index;
    this.errorMessage = '';
  }

  previous(): void {
    if (!this.resultClosed && !this.isBusy && !this.isFirstStep) {
      this.activeStepIndex -= 1;
      this.errorMessage = '';
    }
  }

  saveAndContinue(): void {
    this.saveStep(false);
  }

  skipStep(): void {
    this.saveStep(true);
  }

  dismiss(): void {
    if (this.dismissalStarted || this.resultClosed) {
      return;
    }

    this.dismissalStarted = true;
    const operation: OnboardingDismissSnapshot = Object.freeze({
      generation: this.nextOperationGeneration(),
      kind: 'dismiss',
      body: Object.freeze({ dismissedOn: new Date().toISOString() }),
    });
    this.ownOperation(operation);

    this.onboardingClient
      .dismiss({ body: operation.body })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.finalizeOperation(operation)),
      )
      .subscribe({
        next: () => {
          if (this.isCurrentOperation(operation)) {
            this.closeWithExistingResultContract();
          }
        },
        error: () => {
          if (this.isCurrentOperation(operation)) {
            this.closeWithExistingResultContract();
          }
        },
      });
  }

  hasRecommendationTarget(
    recommendation: UserOnboardingDeviceConnectorRecommendationResponse,
  ): boolean {
    return (
      !!this.externalTarget(recommendation) ||
      !!this.internalTarget(recommendation)
    );
  }

  openRecommendation(
    recommendation: UserOnboardingDeviceConnectorRecommendationResponse,
  ): void {
    if (this.resultClosed || this.isBusy) {
      return;
    }

    this.errorMessage = '';
    const externalTarget = this.externalTarget(recommendation);
    if (externalTarget) {
      const result = this.externalNavigation.openExternal({
        policyKind: ExternalNavigationPolicyKind.AnyHttpHost,
        targetUrl: externalTarget,
      });
      if (result.kind !== 'opened') {
        this.errorMessage =
          'This recommendation could not be opened safely. Check your browser settings and try again.';
      }
      return;
    }

    const internalTarget = this.internalTarget(recommendation);
    if (!internalTarget) {
      this.errorMessage = 'This recommendation is not available right now.';
      return;
    }

    this.navigateInternally(internalTarget);
  }

  openEmergencySettings(): void {
    if (!this.resultClosed && !this.isBusy) {
      this.navigateInternally('/account/people');
    }
  }

  private saveStep(skip: boolean): void {
    if (this.resultClosed || this.isBusy || !this.setup) {
      return;
    }

    const stepIndex = this.activeStepIndex;
    const body = this.captureStepBody(this.activeStep.key, skip);
    const operation: OnboardingStepOperationSnapshot = Object.freeze({
      generation: this.nextOperationGeneration(),
      kind: 'step',
      stepIndex,
      isFinalStep: stepIndex === this.steps.length - 1,
      body,
      completionBody:
        stepIndex === this.steps.length - 1
          ? Object.freeze({ completedOn: new Date().toISOString() })
          : null,
    });
    this.ownOperation(operation);

    this.createStepMutation(operation)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.finalizeOperation(operation)),
      )
      .subscribe((outcome) =>
        this.applyStepMutationOutcome(operation, outcome),
      );
  }

  private createStepMutation(
    operation: OnboardingStepOperationSnapshot,
  ): Observable<StepMutationOutcome> {
    return this.onboardingClient
      .saveStep({
        body: this.toClientStepRequest(operation.body),
      })
      .pipe(
        map((status): StepMutationOutcome => ({ kind: 'saved', status })),
        catchError((): Observable<StepMutationOutcome> =>
          of({
            kind: 'failed',
            phase: 'save',
          }),
        ),
        switchMap((outcome): Observable<StepMutationOutcome> => {
          if (outcome.kind !== 'saved' || !operation.isFinalStep) {
            return of(outcome);
          }
          if (
            !this.isCurrentOperation(operation) ||
            !operation.completionBody
          ) {
            return EMPTY;
          }

          return this.onboardingClient
            .complete({ body: operation.completionBody })
            .pipe(
              map((status): StepMutationOutcome => ({
                kind: 'completed',
                savedStatus: outcome.status,
                status,
              })),
              catchError((): Observable<StepMutationOutcome> =>
                of({
                  kind: 'failed',
                  phase: 'complete',
                  savedStatus: outcome.status,
                }),
              ),
            );
        }),
      );
  }

  private applyStepMutationOutcome(
    operation: OnboardingStepOperationSnapshot,
    outcome: StepMutationOutcome,
  ): void {
    if (!this.isCurrentOperation(operation)) {
      return;
    }

    if (outcome.kind === 'failed') {
      if (outcome.savedStatus) {
        this.applyStatus(outcome.savedStatus);
        this.activeStepIndex = operation.stepIndex;
      }
      this.errorMessage =
        outcome.phase === 'save'
          ? 'Unable to save this setup step.'
          : 'Your setup was saved, but completion failed. Try finishing again.';
      this.requestRender();
      return;
    }

    this.applyStatus(outcome.status);
    if (outcome.kind === 'completed') {
      this.closeWithExistingResultContract();
    } else {
      this.activeStepIndex = Math.min(
        operation.stepIndex + 1,
        this.steps.length - 1,
      );
    }
    this.requestRender();
  }

  private captureStepBody(
    stepKey: OnboardingStepKey,
    skip: boolean,
  ): OnboardingStepBodySnapshot {
    switch (stepKey) {
      case 'proactive-assistant':
        return Object.freeze({
          stepKey,
          skip,
          proactiveAssistantEnabled: this.proactiveAssistantEnabled,
        });
      case 'time-zone':
        return Object.freeze({ stepKey, skip, timeZoneId: this.timeZoneId });
      case 'communication-channels':
        return Object.freeze({
          stepKey,
          skip,
          preferredCommunicationChannels: Object.freeze([
            ...this.selectedChannels,
          ]),
        });
      case 'devices-connectors':
      case 'emergency':
        return Object.freeze({ stepKey, skip });
    }
  }

  private toClientStepRequest(
    body: OnboardingStepBodySnapshot,
  ): SaveUserOnboardingStepRequest {
    return {
      stepKey: body.stepKey,
      skip: body.skip,
      proactiveAssistantEnabled: body.proactiveAssistantEnabled,
      timeZoneId: body.timeZoneId,
      preferredCommunicationChannels: body.preferredCommunicationChannels
        ? [...body.preferredCommunicationChannels]
        : undefined,
    };
  }

  private applyStatus(status: UserOnboardingStatusResponse): void {
    this.status = status;
    this.proactiveAssistantEnabled =
      status.setup?.proactiveAssistantEnabled !== false;
    this.timeZoneId = status.setup?.timeZoneId || this.resolveBrowserTimeZone();
    this.selectedChannels = new Set(
      (status.setup?.communicationChannels ?? []).flatMap((channel) =>
        channel.isSelected && channel.channelKind ? [channel.channelKind] : [],
      ),
    );
    this.selectedChannels.add('InApp');
  }

  private resolveFirstIncompleteStepIndex(
    status: UserOnboardingStatusResponse,
  ): number {
    const completed = new Set([
      ...(status.completedStepKeys ?? []),
      ...(status.skippedStepKeys ?? []),
    ]);
    const index = this.steps.findIndex((step) => !completed.has(step.key));
    return index >= 0 ? index : this.steps.length - 1;
  }

  private resolveBrowserTimeZone(): string {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch {
      return 'UTC';
    }
  }

  private toTimeZoneOptions(
    zones: readonly TimeZone[] | null | undefined,
  ): SelectOption[] {
    return (zones ?? [])
      .map((zone) => ({
        label: zone.displayName || zone.name || zone.id || '',
        value: zone.id || zone.code || '',
      }))
      .filter((option) => !!option.value);
  }

  private externalTarget(
    recommendation: UserOnboardingDeviceConnectorRecommendationResponse,
  ): string | null {
    return (
      recommendation.downloadUrl?.trim() ||
      recommendation.storeUrl?.trim() ||
      null
    );
  }

  private internalTarget(
    recommendation: UserOnboardingDeviceConnectorRecommendationResponse,
  ): string | null {
    const target = recommendation.internalRoute?.trim();
    return target?.startsWith('/') && !target.startsWith('//') ? target : null;
  }

  private navigateInternally(route: string): void {
    const operation: OnboardingNavigationSnapshot = Object.freeze({
      generation: this.nextOperationGeneration(),
      kind: 'navigation',
      route,
    });
    this.ownOperation(operation);

    defer(() => from(this.router.navigateByUrl(operation.route)))
      .pipe(
        catchError(() => of(false)),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.finalizeOperation(operation)),
      )
      .subscribe((navigated) => {
        if (!this.isCurrentOperation(operation)) {
          return;
        }

        if (navigated) {
          this.closeWithExistingResultContract();
        } else {
          this.errorMessage = 'This destination is not available right now.';
        }
        this.requestRender();
      });
  }

  private ownOperation(operation: OnboardingOperationSnapshot): void {
    this.activeOperation = operation;
    this.errorMessage = '';
    this.isSubmitting = operation.kind === 'step';
    this.isNavigating = operation.kind === 'navigation';
    this.isDismissing = operation.kind === 'dismiss';
    this.requestRender();
  }

  private finalizeOperation(operation: OnboardingOperationSnapshot): void {
    if (!this.isCurrentOperation(operation)) {
      return;
    }

    this.activeOperation = null;
    if (operation.kind === 'load') {
      this.isLoading = false;
    } else if (operation.kind === 'step') {
      this.isSubmitting = false;
    } else if (operation.kind === 'navigation') {
      this.isNavigating = false;
    } else {
      this.isDismissing = false;
    }
    this.requestRender();
  }

  private isCurrentOperation(operation: OnboardingOperationSnapshot): boolean {
    return (
      !this.destroyed &&
      this.activeOperation === operation &&
      this.operationGeneration === operation.generation
    );
  }

  private nextOperationGeneration(): number {
    this.operationGeneration += 1;
    return this.operationGeneration;
  }

  private closeWithExistingResultContract(): void {
    if (this.resultClosed || this.destroyed) {
      return;
    }

    this.resultClosed = true;
    this.modalService.close();
  }

  private requestRender(): void {
    if (!this.destroyed) {
      this.changeDetector.markForCheck();
    }
  }
}
